import { CartUpdateEvent } from "@theme/events";

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

// Single source of truth for which values get mirrored into cart attributes.
// To track a new value: add one line here. No other JS changes needed.
const CART_ATTRIBUTE_SOURCES = [
  { attribute: "ma_utm", read: () => getCookie("ma_utm") },
  // Storefront language. A language switch alone doesn't make Shopify recalculate the
  // cart, so discount Function titles (e.g. "Coupon 10% Off") stay in the old language.
  // Writing a changed value forces the recalculation in the new language.
  { attribute: "_language", read: () => window.Theme?.locale?.language },
];

function collectCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll("cart-items-component")) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

async function syncCartAttributes() {
  const updates = {};
  for (const { attribute, read } of CART_ATTRIBUTE_SOURCES) {
    const value = read();
    if (value) updates[attribute] = value;
  }
  if (Object.keys(updates).length === 0) return;

  const cartRes = await fetch("/cart.js");
  const cart = await cartRes.json();

  const alreadySynced = Object.entries(updates).every(([key, value]) => cart.attributes[key] === value);
  if (alreadySynced) return;

  // Ask for the cart sections back in the same round trip so the rendered rows
  // can be refreshed below.
  const body = { attributes: { ...cart.attributes, ...updates } };
  const sectionIds = collectCartSectionIds();
  if (sectionIds.length > 0) {
    body.sections = sectionIds.join(",");
    body.sections_url = window.location.pathname;
  }

  // Use the locale-prefixed route (e.g. /zh-tw/cart/update.js). The unprefixed path runs
  // the recalculation in the primary language, so discount titles come back in English.
  const updateUrl = (window.Theme?.routes?.cart_update_url || "/cart/update") + ".js";
  const updated = await fetch(updateUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
    .then(response => response.json())
    .catch(() => null);
  if (!updated || updated.status) return;

  // Writing attributes makes Shopify re-run discount allocation, and a line item
  // key changes whenever its discounts do. Leaving the rendered rows untouched
  // strands stale data-key values, and the next /cart/change.js POST from those
  // rows fails with "no valid id or line parameter".
  const autoOpenDrawers = Array.from(document.querySelectorAll("cart-drawer-component[auto-open]"));
  for (const drawer of autoOpenDrawers) drawer.removeAttribute("auto-open");

  document.dispatchEvent(
    new CartUpdateEvent(updated, "cart-attribute-sync", {
      itemCount: updated.item_count,
      source: "cart-attribute-sync",
      sections: updated.sections ?? undefined,
    })
  );

  setTimeout(() => {
    for (const drawer of autoOpenDrawers) drawer.setAttribute("auto-open", "");
  }, 0);
}

syncCartAttributes();
